import { useState } from 'react';
import { Mail, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { auth } from '@/lib/api-client';

// Persistent top-of-app banner shown until the user verifies their email.
// Appears for two cases:
//   1. New unverified signup (email_verified_at IS NULL, no pending_email)
//   2. Email-change in progress (pending_email is set — verify the NEW address)
// Hidden once email_verified_at is set AND there's no pending_email.
export const VerificationBanner: React.FC = () => {
  const profile = useAuthStore((s) => s.profile);
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!profile) return null;

  const needsInitialVerify = !profile.email_verified_at && !profile.pending_email;
  const hasPendingChange = !!profile.pending_email;
  if (!needsInitialVerify && !hasPendingChange) return null;

  const targetEmail = hasPendingChange ? profile.pending_email! : profile.email;

  const handleResend = async () => {
    setSending(true);
    setError(null);
    try {
      const result = await auth.resendVerification();
      if (result.already_verified) {
        // Edge case — banner stays visible until profile refreshes elsewhere.
        // Still surface a quick acknowledgement.
        setSentAt(Date.now());
      } else {
        setSentAt(Date.now());
      }
    } catch (err) {
      setError((err as Error).message || 'Could not resend.');
    } finally {
      setSending(false);
    }
  };

  const justSent = sentAt !== null && Date.now() - sentAt < 30_000;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background: hasPendingChange
          ? 'linear-gradient(90deg, rgba(59,130,246,0.12), rgba(123,92,250,0.12))'
          : 'linear-gradient(90deg, rgba(251,191,36,0.12), rgba(251,146,60,0.12))',
        borderBottom: hasPendingChange
          ? '1px solid rgba(59,130,246,0.3)'
          : '1px solid rgba(251,191,36,0.3)',
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        flexWrap: 'wrap',
        fontFamily: "'Outfit', sans-serif",
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 auto', justifyContent: 'center' }}>
        {hasPendingChange ? (
          <Mail size={16} style={{ color: '#60A5FA', flexShrink: 0 }} />
        ) : (
          <AlertTriangle size={16} style={{ color: '#FBBF24', flexShrink: 0 }} />
        )}
        <p style={{ margin: 0, fontSize: 13.5, color: '#F8FAFC', fontWeight: 500 }}>
          {hasPendingChange ? (
            <>
              Verify your new email <strong style={{ color: '#60A5FA' }}>{targetEmail}</strong> to finish the change.
              Your previous email stays active until then.
            </>
          ) : (
            <>
              Please verify <strong style={{ color: '#FBBF24' }}>{targetEmail}</strong> to unlock plan selection
              and your AI manager.
            </>
          )}
        </p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        {error && (
          <span style={{ fontSize: 12, color: '#FCA5A5' }}>{error}</span>
        )}
        {justSent && !error && (
          <span style={{ fontSize: 12, color: '#86EFAC', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <CheckCircle size={12} /> Sent
          </span>
        )}
        <button
          onClick={handleResend}
          disabled={sending || justSent}
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#F8FAFC',
            padding: '5px 14px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: sending || justSent ? 'default' : 'pointer',
            opacity: sending || justSent ? 0.6 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            transition: 'background 0.15s ease',
          }}
        >
          {sending && <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />}
          {sending ? 'Sending…' : justSent ? 'Email sent' : 'Resend email'}
        </button>
      </div>
    </div>
  );
};
